import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Backdrop,
  Box,
  Fade,
  IconButton,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import RichTextViewer from "../../contenido/components/RichTextViewer";

const LabelChip = ({ children }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      px: "20px",
      py: "10px",
      bgcolor: "rgba(242, 243, 249, 1)",
      borderRadius: "33px",
      boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
      flexShrink: 0,
      whiteSpace: "nowrap",
    }}
  >
    <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "rgba(2, 8, 23, 1)" }}>
      {children}
    </Typography>
  </Box>
);

const ValueBox = ({ children, isMultiline = false }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: "10px",
      p: "10px",
      flexGrow: 1,
      bgcolor: "rgba(247, 247, 248, 1)",
      borderRadius: "20px",
      boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
      minHeight: isMultiline ? 92 : "auto",
      maxHeight: isMultiline ? 260 : "none",
      overflow: isMultiline ? "auto" : "hidden",
    }}
  >
    <Typography
      sx={{
        flex: 1,
        fontSize: "16px",
        fontWeight: 400,
        color: "rgba(2, 8, 23, 1)",
        whiteSpace: isMultiline ? "pre-wrap" : "nowrap",
        overflow: "hidden",
        textOverflow: isMultiline ? "clip" : "ellipsis",
      }}
    >
      {children}
    </Typography>
  </Box>
);

const FieldRow = ({ label, value, isMultiline = false, isHorizontal = true }) => {
  if (isHorizontal) {
    return (
      <Stack direction="row" spacing="10px" sx={{ width: "100%", alignItems: "stretch" }}>
        <LabelChip>{label}</LabelChip>
        <ValueBox isMultiline={isMultiline}>{value}</ValueBox>
      </Stack>
    );
  }
  return (
    <Stack spacing="10px" sx={{ width: "100%" }}>
      <LabelChip>{label}</LabelChip>
      <ValueBox isMultiline={isMultiline}>{value}</ValueBox>
    </Stack>
  );
};

function pad2(n) {
  return String(n).padStart(2, "0");
}
function fmtFechaAR(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    const [y, m, da] = String(iso).slice(0, 10).split("-");
    return y && m && da ? `${da}/${m}/${y}` : String(iso);
  }
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

export default function MensajeDetailModal({ open, onClose, message, sucursalNombre, onDelete }) {
  const nombre = message?.titulo ?? "—";
  const tema = message?.tema ?? "—";
  const alcance = message?.alcance ?? "—";

  const contenido =
    message?._raw?.contenido ??
    message?.contenido ??
    message?.preview ??
    "—";

  const fechaPublicacion =
    fmtFechaAR(message?._raw?.fecha_publicacion ?? message?._raw?.fechaPublicacion ?? message?.fecha);

  const showSucursal = String(alcance || "").toLowerCase() === "sucursal";
  const sucursalLabel = sucursalNombre || "—";

  return (
    <Modal
      open={!!open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 180,
          sx: { bgcolor: "rgba(2, 8, 23, 0.45)", backdropFilter: "blur(3px)" },
        },
      }}
    >
      <Fade in={!!open} timeout={180}>
        <Box
          sx={{
            outline: 0,
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Box
            sx={{
              width: "min(92vw, 760px)",
              bgcolor: "neutral.500",
              borderRadius: "30px",
              p: { xs: 2, sm: 3 },
              boxShadow: "0px 20px 60px rgba(0,0,0,.28)",
              position: "relative",
              maxHeight: "85vh",
              overflow: "auto",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                display: "flex",
                gap: 1,
              }}
            >
              <IconButton
                onClick={onDelete}
                disabled={!onDelete}
                sx={{
                  width: 36,
                  height: 36,
                  "&:hover": { bgcolor: "rgba(230, 230, 234, 1)" },
                }}
                title="Eliminar mensaje"
              >
                <DeleteOutlineIcon sx={{ color: "error.main" }} />
              </IconButton>

              <IconButton
                onClick={onClose}
                sx={{
                  width: 36,
                  height: 36,
                  "&:hover": { bgcolor: "rgba(230, 230, 234, 1)" },
                }}
              >
                <CloseIcon sx={{ color: "primary.900" }} />
              </IconButton>
            </Box>

            <Stack alignItems="center" spacing="10px" sx={{ width: "100%" }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  px: "20px",
                  py: "10px",
                  bgcolor: "secondary.50",
                  borderRadius: "33px",
                  boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
                }}
              >
                <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "primary.900" }}>
                  Detalle Mensaje
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing="18px" sx={{ width: "100%", flexWrap: "wrap", mt: 2.5 }}>
              {/* Columna izq */}
              <Stack spacing="20px" sx={{ minWidth: { xs: "100%", sm: "330px" }, flex: 1 }}>
                <FieldRow label="Nombre" value={nombre} isHorizontal />
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <Box sx={{ alignSelf: "flex-start" }}>
                    <LabelChip>Contenido</LabelChip>
                  </Box>
                  <Box sx={{ width: "100%" }}>
                    <RichTextViewer
                      content={contenido}
                      showTitle={false}
                    />
                  </Box>
                </Box>
              </Stack>

              {/* Columna der */}
              <Stack spacing="20px" sx={{ minWidth: { xs: "100%", sm: "300px" }, flex: 1 }}>
                <FieldRow label="Tema" value={tema} isHorizontal />
                <FieldRow label="Alcance" value={alcance} isHorizontal />
                {showSucursal ? <FieldRow label="Sucursal" value={sucursalLabel} isHorizontal /> : null}
                <FieldRow label="Fecha publicación" value={fechaPublicacion} isHorizontal />
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}
